import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import { ValidatedToolDropdownField } from '#src/components/validated-tool-form/shared/ValidatedToolDropdownField.tsx'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface MiniCogFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  '/word-list-version': z.string().nullable(),
  '/word-1-recalled': z.string().nullable(),
  '/word-2-recalled': z.string().nullable(),
  '/word-3-recalled': z.string().nullable(),
  '/clock-drawing': z.string().nullable(),
  summary: z.string().nullable(),
})

interface WordListsLookup {
  [key: string]: [string, string, string]
}

const WORD_LISTS: WordListsLookup = {
  '1': ['Banana', 'Sunrise', 'Chair'],
  '2': ['Leader', 'Season', 'Table'],
  '3': ['Village', 'Kitchen', 'Baby'],
  '4': ['River', 'Nation', 'Finger'],
  '5': ['Captain', 'Garden', 'Picture'],
  '6': ['Daughter', 'Heaven', 'Mountain'],
}

const validatedTool = new ValidatedTool(questionnaires.miniCogQuestionnaire)

export function MiniCogForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: MiniCogFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  const wordListVersion = form.watch('/word-list-version')
  const words = wordListVersion ? WORD_LISTS[wordListVersion] : null

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="3-minute cognitive screening"
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score form={form} response={response} />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}

      <ValidatedToolSection title="Step 1: Word Registration">
        <ValidatedToolDropdownField
          control={form.control}
          name="/word-list-version"
          suggestion={fieldSuggestions?.['/word-list-version']}
          label="Word List Version"
          options={[
            { value: '1', label: 'Version 1: Banana, Sunrise, Chair' },
            { value: '2', label: 'Version 2: Leader, Season, Table' },
            { value: '3', label: 'Version 3: Village, Kitchen, Baby' },
            { value: '4', label: 'Version 4: River, Nation, Finger' },
            { value: '5', label: 'Version 5: Captain, Garden, Picture' },
            { value: '6', label: 'Version 6: Daughter, Heaven, Mountain' },
          ]}
        />
        {words && (
          <div className="mt-2 rounded-md border bg-muted/50 p-3">
            <p className="text-sm font-medium">
              Read these words to the patient:{' '}
              <span className="font-bold">{words.join(', ')}</span>
            </p>
          </div>
        )}
      </ValidatedToolSection>

      <ValidatedToolSection title="Step 2: Clock Drawing">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/clock-drawing"
          suggestion={fieldSuggestions?.['/clock-drawing']}
          options={[
            { value: 'normal', label: 'Normal (2 points)' },
            { value: 'abnormal', label: 'Abnormal (0 points)' },
          ]}
        />
      </ValidatedToolSection>

      <ValidatedToolSection title="Step 3: Word Recall">
        {words ? (
          <div className="space-y-3">
            {(
              [
                '/word-1-recalled',
                '/word-2-recalled',
                '/word-3-recalled',
              ] as const
            ).map((name, i) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field: { value, onChange, ...rest } }) => (
                  <label className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      {...rest}
                      value={value === 'yes'}
                      onChange={(checked) => onChange(checked ? 'yes' : 'no')}
                    />
                    <span className="text-sm">{words[i]}</span>
                  </label>
                )}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a word list version first
          </p>
        )}
      </ValidatedToolSection>

      <Score form={form} response={response} />

      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>

      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  response,
}: {
  form: UseFormReturn<any>
  response?: ValidatedToolResponse
}): JSX.Element | null {
  const valuesByLinkId = form.watch()

  const recallScore =
    (valuesByLinkId['/word-1-recalled'] === 'yes' ? 1 : 0) +
    (valuesByLinkId['/word-2-recalled'] === 'yes' ? 1 : 0) +
    (valuesByLinkId['/word-3-recalled'] === 'yes' ? 1 : 0)

  const clockValue = valuesByLinkId['/clock-drawing']
  const clockScore =
    clockValue === 'normal' ? 2 : clockValue === 'abnormal' ? 0 : null

  if (clockScore === null) {
    return null
  }

  const totalScore = recallScore + clockScore
  const isPositive = totalScore < 3
  const color = isPositive ? 'red' : 'green'
  const label = isPositive
    ? `Positive screen (${totalScore})`
    : `Negative screen (${totalScore})`

  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{totalScore}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}
